import {ButtonProps, Button, Menu, MenuItemProps, useSafeMantineTheme, CheckIcon} from '@mantine/core'
import {ComponentPropsWithoutRef, useState} from 'react'
import {HiPlusCircle} from 'react-icons/hi2'
import {MdVisibilityOff, MdVisibility} from 'react-icons/md'
import {entries} from 'shared/collection'
import classes from './Buttons.module.css'
import {ChevronIcon} from './icons'
import {KbdComboLabel, useKbd, type KbdCombo} from './Keyboard'
import {HiCheck} from 'react-icons/hi'

export const useShowHide = (defaultShow: boolean) => {
  const [showState, setShow] = useState<boolean>()
  const show = showState ?? defaultShow
  return {show, setShow}
}

type UseShowHide = ReturnType<typeof useShowHide>

export const ButtonShowHide = ({show, setShow, ...props}: ButtonProps & UseShowHide) => {
  return (
    <Button
      variant='subtle'
      leftSection={show ? <MdVisibilityOff /> : <MdVisibility />}
      onClick={(e: {preventDefault: () => void; stopPropagation: () => void}) => {
        e.preventDefault()
        e.stopPropagation()
        setShow(!show)
      }}
      {...props}>
      {show ? 'hide' : 'show'}
    </Button>
  )
}

export const ButtonNew = (props: ButtonProps & ComponentPropsWithoutRef<'button'>) => (
  <Button variant='subtle' leftSection={<HiPlusCircle />} {...props}></Button>
)

export const ButtonSplit = <T extends string>({
  button,
  items,
  value,
  onClick,
  onChange,
  showSelected
}: {
  button: (v: T) => Partial<ButtonProps>
  items: Record<T, MenuItemProps>
  value: T
  onClick?: (v: T) => void
  onChange: (v: T) => void
  showSelected?: boolean
}) => {
  const theme = useSafeMantineTheme()
  const {className, children, ...buttonProps} = button(value) ?? {}
  return (
    <div className='flex items-center'>
      <Button
        variant='filled'
        {...buttonProps}
        onClick={() => onClick?.(value)}
        className={`!pr-2 border-r border-white ${classes.button} ${className ?? ''}`}>
        {children}
      </Button>
      <Menu transitionProps={{transition: 'pop'}} position='bottom-end' withinPortal>
        <Menu.Target>
          <Button
            variant='filled'
            color={button(value)?.color ?? theme.primaryColor}
            className={`!px-2 ${classes.menuControl}`}>
            <ChevronIcon />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {entries(items)
            .filter(([k]) => (showSelected ? true : k != value))
            .map(([v, {leftSection, ...props}]) => (
              <Menu.Item
                key={v}
                className={classes.menuItem}
                {...props}
                onClick={() => {
                  onChange?.(v)
                }}
              />
            ))}
        </Menu.Dropdown>
      </Menu>
    </div>
  )
}

export const ctrlEnter = {key: 'Enter', mods: ['Meta']} satisfies KbdCombo
export const ButtonCtrlEnterSection = () => (
  <KbdComboLabel variant='text' combo={ctrlEnter} className='opacity-50 text-xs' />
)

export const useCtrlEnter = ({onClick}: {onClick: () => void}) => {
  const {kbdMatch} = useKbd()
  const isPressed = (e: Pick<KeyboardEvent, 'getModifierState' | 'key'> | Pick<MouseEvent, 'getModifierState'>) =>
    kbdMatch(e, ctrlEnter)
  const onKeyDown = (
    e:
      | Pick<KeyboardEvent, 'getModifierState' | 'key' | 'preventDefault'>
      | Pick<MouseEvent, 'getModifierState' | 'preventDefault'>
  ) => {
    if (isPressed(e)) {
      e.preventDefault()
      onClick()
    }
  }
  const buttonProps = {
    onClick,
    rightSection: <ButtonCtrlEnterSection />,
    variant: 'filled'
  }
  return {onKeyDown, buttonProps}
}
